import type { ReactNode } from "react";
import { CheckCircle2, Info, TriangleAlert, XCircle } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cx.js";

export type AlertType = "info" | "success" | "warning" | "error";

export interface AlertProps {
  type?: AlertType;
  icon?: ReactNode | false;
  className?: string;
  children: ReactNode;
}

/* Tinted surface + a hue-carrying hairline and icon; the prose stays in the
   normal ink. Reads the --az-* semantic tokens rather than raw Tailwind hues
   (sky/emerald/amber/red) so alerts follow the app palette in both themes —
   the old sky-50 info strip was the one blue surface in an indigo product. */
const alertVariants = cva("flex items-start gap-3 rounded-lg border px-4 py-3 text-sm text-foreground", {
  variants: {
    type: {
      info: "border-[var(--az-info-border)] bg-[var(--az-info-bg)]",
      success: "border-[var(--az-success-border)] bg-[var(--az-success-bg)]",
      warning: "border-[var(--az-warning-border)] bg-[var(--az-warning-bg)]",
      error: "border-[var(--az-error-border)] bg-[var(--az-error-bg)]",
    },
  },
  defaultVariants: { type: "info" },
});

const ICONS: Record<AlertType, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  error: XCircle,
};

const ICON_TONE: Record<AlertType, string> = {
  info: "text-[var(--az-info)]",
  success: "text-[var(--az-success)]",
  warning: "text-[var(--az-warning-accent)]",
  error: "text-[var(--az-error-accent)]",
};

export function Alert({ type = "info", icon, className, children }: AlertProps) {
  const DefaultIcon = ICONS[type];
  const showIcon = icon !== false;
  return (
    <div
      role="alert"
      className={cn(alertVariants({ type } satisfies VariantProps<typeof alertVariants>), className)}
    >
      {showIcon && (
        <span className={cn("mt-0.5 shrink-0", ICON_TONE[type])} aria-hidden>
          {icon != null ? icon : <DefaultIcon className="size-4" />}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
